import React, { useState } from 'react';
import {
  Box,
  Pagination,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  Chip,
  Avatar,
  Stack,
  Tabs,
  Tab,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import { 
  Refresh, 
  Search, 
  FilterList, 
  KeyboardArrowDown, 
  KeyboardArrowUp,
  Person,
  Email,
  Phone,
  CalendarToday,
  CheckCircle,
  Cancel,
  Close,
  Send,
  LocalOffer,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListUsersQuery,
} from '../../features/admin/userlistApi.ts';

import {
  useListCouponsQuery,
  Coupon,
} from '../../features/admin/couponApi.ts';

interface User {
  user_id: number;
  first_name: string;
  last_name: string;
  user_email: string;
  user_contact: string;
  status: string;
  member_type: string;
  subscription_date: string | null;
  expiry_date: string | null;
}

const UserList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [searchStr, setSearchStr] = useState('');
  const [filterType, setFilterType] = useState<number>(0);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [openSendCouponDialog, setOpenSendCouponDialog] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectedCoupon, setSelectedCoupon] = useState<number | ''>('');
  const [isSending, setIsSending] = useState(false);

  // API hooks
  const { 
    data, 
    isLoading, 
    error, 
    isError, 
    refetch 
  } = useListUsersQuery({ filter_type: filterType });

  const {
    data: couponsData,
    isLoading: isLoadingCoupons,
  } = useListCouponsQuery();

  // Handle API response structure
  const users = data?.data || [];
  const coupons = couponsData?.data || [];
  const totalRecords = data?.total || users.length;
  
  const usersPerPage = 10;
  const totalPages = Math.ceil(totalRecords / usersPerPage);

  // Filter users based on search
  const filteredUsers = users.filter((user: User) => 
    user.first_name?.toLowerCase().includes(searchStr.toLowerCase()) ||
    user.last_name?.toLowerCase().includes(searchStr.toLowerCase()) ||
    user.user_email?.toLowerCase().includes(searchStr.toLowerCase()) ||
    user.user_contact?.includes(searchStr) ||
    user.user_id.toString().includes(searchStr)
  );

  // Paginate users
  const paginatedUsers = filteredUsers.slice(
    (page - 1) * usersPerPage,
    page * usersPerPage
  );

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStr(event.target.value);
    setPage(1);
  };

  const handleRefresh = async () => {
    try {
      await refetch();
      setSnackbar({
        open: true,
        message: 'Users refreshed successfully!',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Error refreshing users',
        severity: 'error'
      });
    }
  };

  const handleToggleExpand = (userId: number) => {
    setExpandedUserId(expandedUserId === userId ? null : userId);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleFilterChange = (newFilter: number) => {
    setFilterType(newFilter);
    setPage(1);
  };

  // Send Coupon Functions
  const handleOpenSendCouponDialog = () => {
    setOpenSendCouponDialog(true);
    setSelectedUsers([]);
    setSelectedCoupon('');
  };

  const handleCloseSendCouponDialog = () => {
    setOpenSendCouponDialog(false);
    setSelectedUsers([]);
    setSelectedCoupon('');
  };

  const handleUserSelection = (userId: number) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllUsers = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map((user: User) => user.user_id));
    }
  };

  const handleSendCoupon = async () => {
    if (!selectedCoupon || selectedUsers.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select a coupon and at least one user',
        severity: 'error'
      });
      return;
    }

    setIsSending(true);
    try {
      // Send coupon to each selected user
      const sendPromises = selectedUsers.map(userId => 
        fetch('https://fftestservice.sarsetech.com/user/send-coupon', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
            'authkey': '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3'
          },
          body: JSON.stringify({
            coupon_id: selectedCoupon,
            user_id: userId
          })
        })
      );

      const responses = await Promise.all(sendPromises);
      const results = await Promise.all(responses.map(res => res.json()));

      // Check if all requests were successful
      const allSuccess = results.every(result => !result.error);

      if (allSuccess) {
        setSnackbar({
          open: true,
          message: `Coupon sent successfully to ${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''}!`,
          severity: 'success'
        });
        handleCloseSendCouponDialog();
      } else {
        throw new Error('Some coupons failed to send');
      }
    } catch (err) {
      console.error('Failed to send coupons:', err);
      setSnackbar({
        open: true,
        message: 'Error sending coupons. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSending(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getMemberTypeColor = (memberType: string) => {
    switch (memberType?.toLowerCase()) {
      case 'silver':
        return { bgcolor: '#e8eaf6', color: '#3f51b5' };
      case 'gold':
        return { bgcolor: '#fff8e1', color: '#f57c00' };
      case 'platinum':
        return { bgcolor: '#f3e5f5', color: '#7b1fa2' };
      case 'none':
      default:
        return { bgcolor: '#f5f5f5', color: '#616161' };
    }
  };

  const getStatusColor = (status: string) => {
    return status?.toLowerCase() === 'active' 
      ? { bgcolor: '#d1f4e0', color: '#00783e' }
      : { bgcolor: '#ffebee', color: '#c62828' };
  };

  const clearFilters = () => {
    setSearchStr('');
    setFilterType(0);
    setPage(1);
  };

  const getFilterCount = (type: number) => {
    if (type === 0) return users.filter((u: User) => u.member_type?.toLowerCase() === 'none' || !u.member_type).length;
    const typeMap: { [key: number]: string } = { 1: 'silver', 2: 'gold', 3: 'platinum' };
    return users.filter((u: User) => u.member_type?.toLowerCase() === typeMap[type]).length;
  };

  const getCouponDisplayText = (coupon: Coupon) => {
    return `${coupon.code} - ${coupon.discount_type === 'flat' ? `AED ${coupon.discount_value}` : `${coupon.discount_value}%`}`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f1f2f4' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar for desktop */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>
        
        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>
        
        {/* Main content area */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              backgroundColor: '#f1f2f4',
            }}
          >
            <Box sx={{ maxWidth: 1600, mx: 'auto', p: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2
              }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#202223' }}>
                  User Management
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#202223',
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Send />}
                    onClick={handleOpenSendCouponDialog}
                    sx={{
                      bgcolor: '#008060',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      px: 2.5,
                      '&:hover': { bgcolor: '#006e52' }
                    }}
                  >
                    Send Coupon
                  </Button>
                </Box>
              </Box>

              <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', border: '1px solid #e1e3e5', overflow: 'hidden' }}>
                <Box sx={{ borderBottom: '1px solid #e1e3e5' }}>
                  <Tabs 
                    value={filterType} 
                    onChange={(e, v) => handleFilterChange(v)}
                    sx={{
                      px: 2,
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '14px',
                        minHeight: 48,
                        color: '#6d7175',
                      },
                      '& .Mui-selected': {
                        color: '#202223',
                        fontWeight: 600,
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#202223',
                        height: 3,
                      }
                    }}
                  >
                    <Tab label={`Normal (${getFilterCount(0)})`} value={0} />
                    <Tab label={`Silver (${getFilterCount(1)})`} value={1} />
                    <Tab label={`Gold (${getFilterCount(2)})`} value={2} />
                    <Tab label={`Platinum (${getFilterCount(3)})`} value={3} />
                  </Tabs>
                </Box>

                <Box sx={{ p: 2, borderBottom: '1px solid #e1e3e5', display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Search users..."
                    value={searchStr}
                    onChange={handleSearch}
                    fullWidth
                    sx={{ 
                      maxWidth: 400,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        bgcolor: 'white',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: '#6d7175' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<FilterList />}
                    onClick={clearFilters}
                    sx={{
                      textTransform: 'none',
                      borderColor: '#c9cccf',
                      color: '#202223',
                      borderRadius: '8px',
                      fontWeight: 500,
                      '&:hover': {
                        borderColor: '#202223',
                        bgcolor: 'transparent',
                      }
                    }}
                  >
                    Clear
                  </Button>
                </Box>

                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, bgcolor: 'white' }}>
                    <CircularProgress size={32} sx={{ color: '#008060' }} />
                  </Box>
                )}

                {isError && !isLoading && (
                  <Box sx={{ p: 3, bgcolor: 'white' }}>
                    <Alert severity="error">Failed to load users. Please try again.</Alert>
                  </Box>
                )}

                {!isLoading && !isError && paginatedUsers.length === 0 && ( 
                  <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'white' }}>
                    <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 500 }}>
                      No users found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {searchStr ? `No results for "${searchStr}"` : 'Users will appear here when they register'}
                    </Typography>
                    {searchStr && (
                      <Button
                        variant="outlined"
                        onClick={clearFilters}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#c9cccf',
                          color: '#202223',
                          borderRadius: '8px',
                          fontWeight: 500,
                        }}
                      >
                        Clear Search
                      </Button>
                    )}
                  </Box>
                )}

                {!isLoading && paginatedUsers.length > 0 && (
                  <Box sx={{ bgcolor: 'white' }}>
                    {/* Table Header - Desktop */}
                    {!isMobile && (
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: '50px 80px 1fr 200px 140px 100px 120px 120px 120px',
                        gap: 2,
                        px: 2,
                        py: 1.5,
                        borderBottom: '1px solid #e1e3e5',
                        bgcolor: '#fafbfb'
                      }}>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          ID
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          User
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Email
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Contact
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Status
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Membership
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Subscribed
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6d7175', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase' }}>
                          Expires
                        </Typography>
                      </Box>
                    )}

                    {/* Users List */}
                    {paginatedUsers.map((user: User) => (
                      <Box key={user.user_id}>
                        {/* User Row */}
                        <Box
                          sx={!isMobile ? {
                            display: 'grid',
                            gridTemplateColumns: '50px 80px 1fr 200px 140px 100px 120px 120px 120px',
                            gap: 2,
                            px: 2,
                            py: 2,
                            alignItems: 'center',
                            borderBottom: '1px solid #e1e3e5',
                            '&:hover': { bgcolor: '#f9fafb' },
                            transition: 'background-color 0.15s ease',
                            cursor: 'pointer'
                          } : {
                            p: 2,
                            borderBottom: '1px solid #e1e3e5',
                            '&:hover': { bgcolor: '#f9fafb' },
                            cursor: 'pointer'
                          }}
                          onClick={() => handleToggleExpand(user.user_id)}
                        >
                          {!isMobile ? (
                            <>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleExpand(user.user_id);
                                }}
                                sx={{ color: '#6d7175' }}
                              >
                                {expandedUserId === user.user_id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                              </IconButton>

                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                #{user.user_id}
                              </Typography>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar
                                  sx={{ 
                                    width: 32, 
                                    height: 32, 
                                    bgcolor: '#f1f2f4',
                                    color: '#202223',
                                    fontSize: '14px',
                                    fontWeight: 600
                                  }}
                                >
                                  {(user.first_name || 'U')[0].toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#202223' }}>
                                    {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                                  </Typography>
                                </Box>
                              </Box>

                              <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                sx={{ 
                                  fontSize: '12px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                                title={user.user_email}
                              >
                                {user.user_email || '-'}
                              </Typography>

                              <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                                {user.user_contact || '-'}
                              </Typography>

                              <Chip 
                                label={user.status || 'Unknown'} 
                                size="small"
                                icon={user.status?.toLowerCase() === 'active' ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
                                sx={{ 
                                  ...getStatusColor(user.status),
                                  fontWeight: 600,
                                  fontSize: '11px'
                                }}
                              />

                              <Chip 
                                label={user.member_type || 'None'} 
                                size="small"
                                sx={{ 
                                  ...getMemberTypeColor(user.member_type),
                                  fontWeight: 600,
                                  fontSize: '11px'
                                }}
                              />

                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                {formatDate(user.subscription_date)}
                              </Typography>

                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px' }}>
                                {formatDate(user.expiry_date)}
                              </Typography>
                            </>
                          ) : (
                            // Mobile View
                            <Stack spacing={2}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Avatar
                                    sx={{ 
                                      width: 40, 
                                      height: 40, 
                                      bgcolor: '#f1f2f4',
                                      color: '#202223',
                                      fontWeight: 600
                                    }}
                                  >
                                    {(user.first_name || 'U')[0].toUpperCase()}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#202223' }}>
                                      {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      ID: {user.user_id}
                                    </Typography>
                                  </Box>
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleExpand(user.user_id);
                                  }}
                                  sx={{ color: '#6d7175' }}
                                >
                                  {expandedUserId === user.user_id ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                </IconButton>
                              </Box>

                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip 
                                  label={user.status || 'Unknown'} 
                                  size="small"
                                  icon={user.status?.toLowerCase() === 'active' ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
                                  sx={{ 
                                    ...getStatusColor(user.status),
                                    fontWeight: 600
                                  }}
                                />
                                <Chip 
                                  label={user.member_type || 'None'} 
                                  size="small"
                                  sx={{ 
                                    ...getMemberTypeColor(user.member_type),
                                    fontWeight: 600
                                  }}
                                />
                              </Box>
                            </Stack>
                          )}
                        </Box>

                        {/* Expanded User Details */}
                        <Collapse in={expandedUserId === user.user_id} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 3, bgcolor: '#fafbfb', borderBottom: '1px solid #e1e3e5' }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#202223' }}>
                             User Details
                            </Typography>
                            
                            <Stack spacing={2}>
                              <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                  <Email sx={{ color: '#6d7175' }} />
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Email Address
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-all' }}>
                                      {user.user_email || '-'}
                                    </Typography>
                                  </Box>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Phone sx={{ color: '#6d7175' }} />
                                  <Box>
                                    <Typography variant="caption" color="text.secondary">
                                      Contact Number
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                                      {user.user_contact || '-'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Card>

                              {(user.subscription_date || user.expiry_date) && (
                                <Card sx={{ p: 2, borderRadius: '8px', border: '1px solid #e1e3e5' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <CalendarToday sx={{ color: '#6d7175' }} />
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="caption" color="text.secondary">
                                        Subscription Date
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {formatDate(user.subscription_date)}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <CalendarToday sx={{ color: '#6d7175' }} />
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="caption" color="text.secondary">
                                        Expiry Date
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {formatDate(user.expiry_date)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Card>
                              )}
                            </Stack>
                          </Box>
                        </Collapse>
                      </Box>
                    ))}
                  </Box>
                )}
              </Card>

              {totalPages > 1 && !isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handleChangePage}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        borderRadius: '8px',
                        fontWeight: 500,
                      },
                      '& .Mui-selected': {
                        bgcolor: '#202223 !important',
                        color: 'white',
                      }
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Send Coupon Dialog */}
      <Dialog
        open={openSendCouponDialog}
        onClose={handleCloseSendCouponDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#202223' }}>
              Send coupon to Users
            </Typography>
            <IconButton onClick={handleCloseSendCouponDialog} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {/* Coupon Selection */}
            <FormControl fullWidth>
              <InputLabel>Select Coupon *</InputLabel>
              <Select
                value={selectedCoupon}
                label="Select Coupon *"
                onChange={(e) => setSelectedCoupon(e.target.value as number)}
                disabled={isLoadingCoupons}
              >
                {coupons.map((coupon: Coupon) => (
                  <MenuItem key={coupon.id} value={coupon.id}>
                    {getCouponDisplayText(coupon)}
                  </MenuItem>
                ))}
              </Select>
              {isLoadingCoupons && (
                <Typography variant="caption" color="text.secondary">
                  Loading Coupon...
                </Typography>
              )}
            </FormControl>

            {/* User Selection */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Select Users ({selectedUsers.length} selected)
                </Typography>
                <Button
                  onClick={handleSelectAllUsers}
                  sx={{ textTransform: 'none' }}
                >
                  {selectedUsers.length === filteredUsers.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>

              <Card sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e1e3e5' }}>
                <List>
                  {filteredUsers.map((user: User) => (
                    <ListItem
                      key={user.user_id}
                      dense
                      button
                      onClick={() => handleUserSelection(user.user_id)}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedUsers.includes(user.user_id)}
                          tabIndex={-1}
                          disableRipple
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {user.user_email}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              ID: {user.user_id} | {user.user_contact}
                            </Typography>
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Chip 
                          label={user.member_type || 'None'} 
                          size="small"
                          sx={{ 
                            ...getMemberTypeColor(user.member_type),
                            fontWeight: 600,
                            fontSize: '10px'
                          }}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Card>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleCloseSendCouponDialog}
            disabled={isSending}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              color: '#202223',
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSendCoupon}
            variant="contained"
            disabled={isSending || !selectedCoupon || selectedUsers.length === 0}
            startIcon={<Send />}
            sx={{
              bgcolor: '#008060',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#006e52' }
            }}
          >
            {isSending ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                 Sending...
              </Box>
            ) : (
              `Send to ${selectedUsers.length} User${selectedUsers.length !== 1 ? 's' : ''}`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserList;  